import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {LicenseStatus} from "../models/license.models";
import {licenseService} from "../services/license.service";
import axios from "axios";

// --- Types ---

type LicenseContextValue = {
    status: LicenseStatus;
    revalidateLicense: () => Promise<void>;
};

// --- Context ---

const LicenseContext = createContext<LicenseContextValue | null>(null);

// --- Provider ---

export const LicenseProvider = ({ children }: { children: ReactNode }) => {
    const [status, setStatus] = useState<LicenseStatus>(LicenseStatus.CHECKING);

    const revalidateLicense = useCallback(async (signal?: AbortSignal) => {
        setStatus(LicenseStatus.CHECKING);

        try {
            await licenseService.validateLicenseStatus(signal);
            setStatus(LicenseStatus.VALID);
        } catch (error) {
            if (axios.isCancel(error)) return;
            setStatus(axios.isAxiosError(error) && error.response ? LicenseStatus.INVALID : LicenseStatus.ERROR);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        revalidateLicense(controller.signal);

        return () => controller.abort();
    }, [revalidateLicense]);

    const value = useMemo(() => ({ status, revalidateLicense }), [status, revalidateLicense]);

    return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>;
};

// --- Hook ---

export const useLicenseValidation = () => {
    const ctx = useContext(LicenseContext);
    if (!ctx) throw new Error('useLicenseValidation must be used within LicenseProvider');
    return ctx;
};